import React, { useMemo } from 'react';
import {
    NSButton,
    NSDropdown, NSLabel,
} from 'bricks';
import NSModal, { INSModalProps } from 'bricks/NSModal/NSModal';
import { IDealDirectory, IndexedFile } from 'views/DocumentLibrary/types';
import { extractDirectories, getFileIcon, normalizeFileFormat } from 'views/DocumentLibrary/helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faFolder } from '@fortawesome/pro-regular-svg-icons';
import DropzoneCard from '../DropzoneCard/DropzoneCard';
import './UploadDocumentModal.scss';

export interface IUploadDocumentLibrary extends INSModalProps {
    onConfirm: () => void;
    directoryTree?: IDealDirectory
    getFilesFromEvent: (event: React.DragEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>) => Promise<File[]>;
    filesToUpload: IndexedFile[];
    setFilesToUpload: (files: IndexedFile[]) => void;
    selectedDirectoryId: number;
    setSelectedDirectoryId: (id: number) => void;
    newFoldersToDisplay: string[];
    setNewFoldersToDisplay: React.Dispatch<React.SetStateAction<string[]>>
    newFilesToDisplay: string[];
    setNewFilesToDisplay: React.Dispatch<React.SetStateAction<string[]>>
}

const UploadDocumentLibrary = ({
    onConfirm,
    directoryTree,
    getFilesFromEvent,
    filesToUpload,
    setFilesToUpload,
    selectedDirectoryId,
    setSelectedDirectoryId,
    newFoldersToDisplay,
    setNewFoldersToDisplay,
    newFilesToDisplay,
    setNewFilesToDisplay,
    ...props
}: IUploadDocumentLibrary) => {
    const directoryOptions = useMemo(() => {
        if (directoryTree) {
            return extractDirectories(directoryTree, true);
        }
        return [];
    }, [directoryTree]);

    const handleCancel = () => {
        setFilesToUpload([]);
        setNewFoldersToDisplay([]);
        setNewFilesToDisplay([]);
        setSelectedDirectoryId(0);
        props.toggle();
    };

    return (
        <NSModal
            {...props}
            modalHeaderText="Upload file"
            modalHeaderClassName="bg-primary text-white"
            modalFooterButtonText="Confirm"
            modalFooterCancelButtonText="Cancel"
            modalFooterButtonColor="primary"
            modalFooterButtonFunction={onConfirm}
            className="UploadDocumentModal"
            customFooter={(
                <div>
                    <NSButton text="Cancel" callback={handleCancel} outline />
                    <NSButton className="mx-2" color="primary" text="Confirm" callback={onConfirm} disabled={filesToUpload.length === 0} />
                </div>
            )}
        >
            <div className="pt-2">
                <DropzoneCard getFilesFromEvent={getFilesFromEvent} accept="*" />
                {newFoldersToDisplay.map(folder => (
                    <div key={folder} className="UploadDocumentModal__file d-flex my-2 px-2 rounded">
                        <FontAwesomeIcon icon={faFolder} className="text-white mr-2" />
                        <span>{folder}</span>
                        <NSButton
                            className="cursor--pointer"
                            icon={faClose}
                            callback={() => setNewFoldersToDisplay(prev => prev.filter(f => f !== folder))}
                        />
                    </div>
                ))}
                {newFilesToDisplay.map(file => (
                    <div key={file} className="UploadDocumentModal__file d-flex my-2 px-2 rounded">
                        <FontAwesomeIcon
                            icon={getFileIcon({ fileFormat: normalizeFileFormat(file), isExpanded: false })}
                            className="text-white mr-2"
                        />
                        <span>{file}</span>
                        <NSButton
                            className="cursor--pointer"
                            icon={faClose}
                            callback={() => {
                                setNewFilesToDisplay(prev => prev.filter(f => f !== file));
                            }}
                        />
                    </div>
                ))}

            </div>
            <div className="separator mb-2" />
            <NSLabel>Destination</NSLabel>
            <NSDropdown
                options={directoryOptions || []}
                selectedOption={directoryOptions?.find(f => f.value === selectedDirectoryId)}
                handleSelectOption={option => {
                    if (typeof option.value === 'number') {
                        setSelectedDirectoryId(option.value);
                    }
                }}
                placeholder="Select folder"
                isFullWidth
            />
        </NSModal>
    );
};

export default UploadDocumentLibrary;
